import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useConnectionState,
  useVoiceAssistant,
  useLocalParticipant,
  VideoTrack,
  BarVisualizer,
  StartAudio,
  PreJoin,
} from '@livekit/components-react';
import { Track, RoomEvent } from 'livekit-client';
import { useRoomContext } from '@livekit/components-react';
import '@livekit/components-styles';

import { setCandidateToken, getAgentToken } from '../lib/interviewApi';
import CodingPanel from '../components/CodingPanel';

// Apriora-style live interview page (Week 2 Day 2 - split layout).
//
// Loaded at /interview/live/by-id/:interviewId?token=<share_token>
//   1. Pins the share_token from the URL so backend calls authenticate
//   2. Fetches a LiveKit JWT from POST /api/interviews/<id>/agent-token
//   3. Connects to wss://meet.dutyai.in via @livekit/components-react
//
// Layout: big candidate webcam left, transcript panel right, AI agent tile
// floating bottom-right as PiP with a speaking-state bar visualizer.

export default function InterviewLivePage() {
  const { interviewId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('loading'); // loading | preflight | inRoom | error
  const [error, setError] = useState('');
  const [creds, setCreds] = useState(null); // { url, token, room }
  const [userChoices, setUserChoices] = useState(null); // LocalUserChoices from PreJoin

  useEffect(() => {
    // Pin the candidate share_token so the backend Auth: Bearer header
    // attaches on the /agent-token call.
    setCandidateToken(searchParams.get('token'));
  }, [searchParams]);

  useEffect(() => {
    if (!interviewId) {
      setError('No interview ID in URL');
      setPhase('error');
      return;
    }
    (async () => {
      try {
        const out = await getAgentToken(interviewId);
        if (!out?.token || !out?.url) {
          throw new Error('Agent-token endpoint returned an unexpected shape');
        }
        setCreds(out);
        setPhase('preflight');
      } catch (e) {
        setError(e.message || 'Failed to get room access token');
        setPhase('error');
      }
    })();
  }, [interviewId]);

  if (phase === 'loading') {
    return <FullPageStatus title="Preparing your interview..." subtitle="Connecting to the AI interviewer" />;
  }
  if (phase === 'error') {
    // Self-serve fallback: if voice failed (broken mic, missing camera,
    // LiveKit blocked by corp firewall), let the candidate jump to the
    // text-only interview at /interview/text/by-id/<id>?token=<same>.
    // Same backend Q&A; no LiveKit dependency. Preserves the share_token.
    const token = searchParams.get('token') || '';
    const textHref = `/interview/text/by-id/${encodeURIComponent(interviewId)}` +
                     (token ? `?token=${encodeURIComponent(token)}` : '');
    return (
      <FullPageStatus
        title="Could not start the voice interview"
        subtitle={error}
        action={{ label: 'Switch to text interview', onClick: () => navigate(textHref) }}
        isError
      />
    );
  }

  // Pre-flight device check: PreJoin shows camera preview + mic level meter
  // + device selection + a Join button. We only mount LiveKitRoom (which
  // triggers the agent dispatch) after the candidate has confirmed their
  // devices work. Reduces wasted dispatches from candidates who join with
  // a broken mic and silently leave.
  if (phase === 'preflight') {
    return (
      <PreflightScreen
        onSubmit={(choices) => {
          setUserChoices(choices);
          setPhase('inRoom');
        }}
        onError={(e) => {
          setError(e.message || 'Device check failed');
          setPhase('error');
        }}
      />
    );
  }

  return (
    <LiveKitRoom
      serverUrl={creds.url}
      token={creds.token}
      connect
      audio={userChoices?.audioEnabled !== false}
      video={userChoices?.videoEnabled !== false}
      connectOptions={{
        // Pass device selections from PreJoin so LiveKit publishes from
        // the same camera/mic the candidate just previewed.
        audioCaptureDefaults: userChoices?.audioDeviceId
          ? { deviceId: userChoices.audioDeviceId }
          : undefined,
        videoCaptureDefaults: userChoices?.videoDeviceId
          ? { deviceId: userChoices.videoDeviceId }
          : undefined,
      }}
      onError={(e) => {
        setError(e.message || String(e));
        setPhase('error');
      }}
      data-lk-theme="default"
      style={{ minHeight: '100vh', background: '#0d1f17' }}
    >
      {/* Mounts every remote audio track to a hidden <audio>. Without this
          the candidate would not hear the agent. */}
      <RoomAudioRenderer />

      {/* Chrome / Safari block autoplay until the user clicks something.
          StartAudio renders a small "Click to enable audio" button if
          autoplay is blocked, then disappears once permission is granted. */}
      <StartAudio label="Click to enable audio" />

      <LiveRoomView roomName={creds.room} interviewId={interviewId} />
    </LiveKitRoom>
  );
}


// ---- Inner view (must be a child of LiveKitRoom to use the hooks) ----------

function LiveRoomView({ roomName, interviewId }) {
  const connectionState = useConnectionState();
  const { localParticipant } = useLocalParticipant();
  // Coding panel visibility. Candidate-toggleable via TopBar + auto-opens
  // when the AI agent dispatches a coding question through the LiveKit
  // data channel (see code_question handler below).
  const [showCoding, setShowCoding] = useState(false);
  // Coding question dispatched by the agent: {questionId, language, prompt, starter}.
  // When set, CodingPanel renders the prompt above the editor + prefills
  // the language + starter. Cleared once the candidate submits.
  const [codingQuestion, setCodingQuestion] = useState(null);
  const room = useRoomContext();

  // Listen for agent-dispatched coding questions over the LiveKit data
  // channel. Payload is a JSON object with `kind` discriminator:
  //   { kind: "code_question", question_id, language, prompt, starter? }
  //   { kind: "code_question_close" }  - agent has accepted the answer
  // Anything else is ignored so we never break for unknown future kinds.
  useEffect(() => {
    if (!room) return;
    const handler = (payload, participant) => {
      // Only trust messages from the agent participant - candidates can
      // also publish data (we use it for code_answer below) but they
      // shouldn't be able to push themselves a question.
      const isAgent = (participant?.identity || '').startsWith('agent-');
      if (!isAgent) return;
      let msg;
      try { msg = JSON.parse(new TextDecoder().decode(payload)); } catch { return; }
      if (msg?.kind === 'code_question') {
        setCodingQuestion({
          questionId: msg.question_id,
          language: msg.language || 'python',
          prompt: msg.prompt || '',
          starter: msg.starter || '',
        });
        setShowCoding(true);
      } else if (msg?.kind === 'code_question_close') {
        setCodingQuestion(null);
        setShowCoding(false);
      }
    };
    room.on(RoomEvent.DataReceived, handler);
    return () => { room.off(RoomEvent.DataReceived, handler); };
  }, [room]);

  // Publish a code_answer data message to the agent. Called from CodingPanel
  // when the candidate clicks "Submit answer". The submission is ALSO
  // persisted via /code-submission (CodingPanel does that); this data
  // message is the agent's signal to move on to the next question.
  const publishCodeAnswer = async ({ language, source_code, questionId }) => {
    if (!localParticipant) return;
    const payload = new TextEncoder().encode(JSON.stringify({
      kind: 'code_answer',
      question_id: questionId,
      language,
      source_code,
    }));
    try {
      // 'reliable' so the answer always lands - we're not in a hot loop.
      await localParticipant.publishData(payload, { reliable: true });
    } catch (e) {
      // Non-fatal - the submission still lives in /code-submission.
      // Agent will poll the DB as a fallback in Phase 3d.
    }
  };

  // ---- Anti-cheat (per Arun's explicit ask, MVP for 1 Aug launch) ----
  // The browser-side monitor watches for tab switching, paste, devtools,
  // fullscreen exit, copy, and typing-cadence anomalies. Each signal is
  // forwarded to the agent over the LiveKit data channel under
  // kind: "anti_cheat" so the agent can verbally call out severe events
  // and mark the interview for HR review at completion. We capture
  // metadata only (lengths, durations, severities) - NEVER the
  // candidate's typed content or clipboard payloads, so we stay
  // compliant with NYC LL144 + EU AI Act transparency rules. Disclosure
  // banner is rendered on the PreflightScreen before any monitoring
  // starts.
  useEffect(() => {
    if (!localParticipant) return;
    let monitor;
    let cancelled = false;
    (async () => {
      const { startAntiCheat } = await import('../lib/antiCheat');
      if (cancelled) return;
      monitor = startAntiCheat({
        onSignal: async (sig) => {
          try {
            const payload = new TextEncoder().encode(
              JSON.stringify({ kind: 'anti_cheat', signal: sig })
            );
            // 'lossy' is fine for the high-frequency low-severity events;
            // 'reliable' is reserved for code_answer.
            await localParticipant.publishData(payload, { reliable: false });
          } catch { /* never let publish failures break monitoring */ }
        },
      });
    })();
    return () => {
      cancelled = true;
      if (monitor) monitor.stop();
    };
  }, [localParticipant]);

  // Candidate's camera track. With <LiveKitRoom video> on the parent,
  // LiveKit auto-publishes the camera once the user grants permission.
  const cameraTracks = useTracks([Track.Source.Camera], { onlySubscribed: false });
  const localCamera = cameraTracks.find(
    (t) => t.participant?.identity === localParticipant?.identity,
  );

  // The AI agent participant + its current state (listening / thinking / speaking).
  // useVoiceAssistant scans the room for the first non-local participant; our
  // LiveKit Agents worker joins with identity 'agent-<sid>' so this picks it up.
  const { agent: agentParticipant, state: agentState, audioTrack: agentAudioTrack } =
    useVoiceAssistant();

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      color: '#fdfaf0',
      fontFamily: 'DM Sans, system-ui, sans-serif',
    }}>
      <TopBar
        roomName={roomName}
        connectionState={connectionState}
        showCoding={showCoding}
        onToggleCoding={() => setShowCoding((v) => !v)}
      />

      <div className="lk-stage-grid" style={{
        flex: 1, gap: 16, padding: 16, maxWidth: 1400, width: '100%', margin: '0 auto',
      }}>
        <CandidateStage localCamera={localCamera} agentState={agentState} />
        {showCoding ? (
          <CodingPanel
            interviewId={interviewId}
            codingQuestion={codingQuestion}
            onSubmitAnswer={publishCodeAnswer}
            onClose={() => setShowCoding(false)}
          />
        ) : (
          <TranscriptPanel localIdentity={localParticipant?.identity} agentIdentity={agentParticipant?.identity} />
        )}
      </div>

      <AgentPiP agentState={agentState} agentAudioTrack={agentAudioTrack} />

      {/* Responsive layout: desktop = 2 columns (webcam left, transcript right);
          tablet/mobile = single column stacked. AgentPiP shrinks on small
          screens so it doesn't cover the transcript. */}
      <style>{`
        .lk-stage-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 380px;
        }
        @media (max-width: 900px) {
          .lk-stage-grid {
            grid-template-columns: 1fr;
            grid-auto-rows: min-content;
          }
        }
        @media (max-width: 600px) {
          .lk-agent-pip {
            width: 160px !important;
            height: 120px !important;
            bottom: 12px !important;
            right: 12px !important;
            padding: 10px !important;
          }
          .lk-agent-pip .lk-pip-avatar {
            width: 40px !important;
            height: 40px !important;
          }
          .lk-agent-pip .lk-pip-bars {
            height: 20px !important;
          }
        }
      `}</style>
    </div>
  );
}


// ---- Top bar: status pill, room name -------------------------------------

function TopBar({ roomName, connectionState, showCoding, onToggleCoding }) {
  const color = connectionState === 'connected' ? '#9fe5b3'
    : connectionState === 'connecting' || connectionState === 'reconnecting' ? '#f0c75e'
    : '#ff9d8a';
  return (
    <header style={{
      padding: '12px 20px',
      borderBottom: '1px solid rgba(253, 250, 240, 0.12)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: 'rgba(0,0,0,0.18)',
    }}>
      <div>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Duty AI Interview</div>
        <div style={{ fontSize: 11, color: '#a8841f', fontStyle: 'italic', marginTop: 2 }}>{roomName}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {onToggleCoding && (
          <button
            onClick={onToggleCoding}
            title={showCoding ? 'Show transcript' : 'Show coding panel'}
            style={{
              background: showCoding ? '#a8841f' : 'transparent',
              color: showCoding ? '#1b3a2a' : '#fdfaf0',
              border: '1px solid rgba(168, 132, 31, 0.55)',
              padding: '5px 12px',
              borderRadius: 6,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {showCoding ? 'Show transcript' : 'Show code editor'}
          </button>
        )}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '4px 12px', background: 'rgba(255,255,255,0.06)',
          borderRadius: 16, fontSize: 12,
        }}>
          <span style={{ width: 8, height: 8, borderRadius: 4, background: color }} />
          <span>{connectionState}</span>
        </div>
      </div>
    </header>
  );
}


// ---- Candidate webcam stage (left, big) -----------------------------------

function CandidateStage({ localCamera, agentState }) {
  return (
    <section style={{
      position: 'relative',
      background: '#000',
      borderRadius: 16,
      overflow: 'hidden',
      minHeight: 480,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {localCamera && localCamera.publication?.track ? (
        <VideoTrack trackRef={localCamera} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <div style={{ color: 'rgba(253, 250, 240, 0.55)', fontSize: 14, padding: 24, textAlign: 'center' }}>
          Camera will appear here once you allow access.
          <br />
          <span style={{ fontSize: 11, color: 'rgba(253, 250, 240, 0.35)' }}>
            Audio-only also works - the agent does not need to see you.
          </span>
        </div>
      )}

      {/* Agent state pill, top-left of the stage */}
      <div style={{
        position: 'absolute', top: 12, left: 12,
        padding: '4px 10px', borderRadius: 12,
        background: 'rgba(0,0,0,0.55)', color: '#fdfaf0',
        fontSize: 11, display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: 3,
          background: agentState === 'speaking' ? '#9fe5b3'
                    : agentState === 'thinking' ? '#f0c75e'
                    : agentState === 'listening' ? '#fdfaf0'
                    : '#888',
          animation: agentState === 'speaking' ? 'lk-pulse 1.2s infinite' : 'none',
        }} />
        AI is {agentState || 'connecting'}
      </div>

      <style>{`
        @keyframes lk-pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </section>
  );
}


// ---- Transcript panel (right column) --------------------------------------

function TranscriptPanel({ localIdentity, agentIdentity }) {
  const [segments, setSegments] = useState([]); // {id, speaker, text, final}
  const room = useRoomContext();
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!room) return;
    const handler = (raw, participant) => {
      const who = participant?.identity || 'unknown';
      const speaker = who === localIdentity ? 'you' : (who.startsWith('agent') ? 'ai' : who);
      setSegments((prev) => {
        const next = [...prev];
        for (const seg of raw) {
          const idx = next.findIndex((s) => s.id === seg.id);
          const item = { id: seg.id, speaker, text: seg.text, final: seg.final };
          if (idx >= 0) next[idx] = item;
          else next.push(item);
        }
        return next;
      });
    };
    room.on(RoomEvent.TranscriptionReceived, handler);
    return () => { room.off(RoomEvent.TranscriptionReceived, handler); };
  }, [room, localIdentity, agentIdentity]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [segments]);

  return (
    <aside style={{
      background: 'rgba(0,0,0,0.25)',
      border: '1px solid rgba(253, 250, 240, 0.12)',
      borderRadius: 16,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 480,
    }}>
      <h2 style={{
        margin: '0 0 10px', fontSize: 13, color: '#a8841f',
        textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 600,
      }}>
        Live transcript
      </h2>
      <div ref={scrollRef} style={{
        flex: 1, overflowY: 'auto', minHeight: 0,
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        {segments.length === 0 ? (
          <div style={{ color: 'rgba(253, 250, 240, 0.45)', fontSize: 12, padding: 12, textAlign: 'center' }}>
            Transcript will appear here as the conversation begins.
          </div>
        ) : (
          segments.map((seg) => (
            <TranscriptItem key={seg.id} {...seg} />
          ))
        )}
      </div>
    </aside>
  );
}

function TranscriptItem({ speaker, text, final }) {
  const isAi = speaker === 'ai';
  return (
    <div style={{
      padding: '8px 12px',
      borderRadius: 10,
      background: isAi ? 'rgba(168, 132, 31, 0.15)' : 'rgba(159, 229, 179, 0.10)',
      border: `1px solid ${isAi ? 'rgba(168, 132, 31, 0.35)' : 'rgba(159, 229, 179, 0.25)'}`,
      opacity: final ? 1 : 0.7,
    }}>
      <div style={{
        fontSize: 10, textTransform: 'uppercase', letterSpacing: 1,
        color: isAi ? '#f0c75e' : '#9fe5b3', marginBottom: 4, fontWeight: 600,
      }}>
        {isAi ? 'AI Interviewer' : 'You'}{!final && ' ...'}
      </div>
      <div style={{ fontSize: 13, color: '#fdfaf0', lineHeight: 1.4 }}>{text}</div>
    </div>
  );
}


// ---- Floating PiP for the AI agent (bottom-right) -------------------------

function AgentPiP({ agentState, agentAudioTrack }) {
  const isSpeaking = agentState === 'speaking';
  const isThinking = agentState === 'thinking';
  const isListening = agentState === 'listening';

  const stateColor =
    isSpeaking ? '#9fe5b3'
    : isThinking ? '#f0c75e'
    : isListening ? '#fdfaf0'
    : '#888';

  return (
    <div className="lk-agent-pip" style={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      width: 240,
      height: 168,
      background: 'linear-gradient(135deg, #1b3a2a, #0d1f17)',
      border: '2px solid rgba(168, 132, 31, 0.55)',
      borderRadius: 16,
      boxShadow: '0 10px 28px rgba(0,0,0,0.45)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 10,
      padding: 14,
      zIndex: 50,
      transition: 'border-color 250ms, box-shadow 250ms',
      ...(isSpeaking ? { borderColor: 'rgba(159, 229, 179, 0.7)' } : {}),
    }}>
      {/* Avatar with concentric pulsing rings on 'speaking' state.
          Rings are absolutely positioned around the avatar so the
          BarVisualizer below doesn't shift when state changes. */}
      <div style={{ position: 'relative', width: 56, height: 56 }}>
        {isSpeaking && (
          <>
            <span style={{
              position: 'absolute', inset: -10, borderRadius: '50%',
              border: '2px solid rgba(159, 229, 179, 0.45)',
              animation: 'lk-pip-ring 1.6s ease-out infinite',
            }} />
            <span style={{
              position: 'absolute', inset: -10, borderRadius: '50%',
              border: '2px solid rgba(159, 229, 179, 0.25)',
              animation: 'lk-pip-ring 1.6s ease-out infinite 0.8s',
            }} />
          </>
        )}
        <div className="lk-pip-avatar" style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          background: isSpeaking
            ? 'radial-gradient(circle at 30% 30%, #c9a648, #a8841f)'
            : 'radial-gradient(circle at 30% 30%, #a8841f, #8a6a17)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20, fontWeight: 700, color: '#1b3a2a',
          letterSpacing: 0.5,
          transition: 'background 250ms',
        }}>
          AI
        </div>
      </div>

      {/* BarVisualizer always renders so the PiP layout doesn't jump when
          the agent track first attaches. Without a track it shows static
          placeholder bars. */}
      <div className="lk-pip-bars" style={{ width: '100%', height: 28 }}>
        <BarVisualizer
          state={agentState}
          barCount={14}
          trackRef={agentAudioTrack}
          style={{ '--lk-fg': isSpeaking ? '#9fe5b3' : '#a8841f' }}
        />
      </div>

      <div style={{
        fontSize: 11, color: '#fdfaf0', opacity: 0.9,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{
          display: 'inline-block', width: 6, height: 6, borderRadius: 3,
          background: stateColor,
          animation: isSpeaking ? 'lk-pip-dot 1.2s infinite' : 'none',
        }} />
        {isSpeaking ? 'Speaking'
         : isThinking ? 'Thinking'
         : isListening ? 'Listening'
         : 'Connecting'}
      </div>

      <style>{`
        @keyframes lk-pip-ring {
          0%   { transform: scale(0.85); opacity: 0.9; }
          100% { transform: scale(1.45); opacity: 0; }
        }
        @keyframes lk-pip-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.5; transform: scale(1.4); }
        }
      `}</style>
    </div>
  );
}


// ---- Pre-flight device check (camera + mic test before joining the room) --

function PreflightScreen({ onSubmit, onError }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#0d1f17',
      color: '#fdfaf0',
      fontFamily: 'DM Sans, system-ui, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{ maxWidth: 720, width: '100%', textAlign: 'center', marginBottom: 18 }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 24, color: '#fdfaf0' }}>
          Duty AI - Interview Check
        </h1>
        <p style={{ margin: 0, color: '#a8841f', fontStyle: 'italic', fontSize: 13 }}>
          Test your camera + microphone, then click Join when you are ready.
          The AI interviewer is waiting on the other side.
        </p>
      </div>

      {/* LiveKit's PreJoin component does the heavy lifting:
            - Camera preview
            - Microphone level meter (proves the mic actually captures audio)
            - Camera + microphone device dropdowns
            - Username input (we override with 'Candidate' but allow change)
            - Join button (calls onSubmit with LocalUserChoices)
          We wrap it so the dark theme + brand styling apply. */}
      <div
        data-lk-theme="default"
        style={{
          width: '100%', maxWidth: 720,
          background: 'rgba(0,0,0,0.25)',
          border: '1px solid rgba(253, 250, 240, 0.12)',
          borderRadius: 16,
          padding: 18,
        }}
      >
        <PreJoin
          defaults={{ username: 'Candidate', videoEnabled: true, audioEnabled: true }}
          onSubmit={onSubmit}
          onError={onError}
          joinLabel="Join Interview"
        />
      </div>

      <p style={{
        marginTop: 16, fontSize: 11, color: 'rgba(253, 250, 240, 0.55)',
        maxWidth: 520, textAlign: 'center', lineHeight: 1.5,
      }}>
        Tip: if your microphone level bar does not move when you speak, click the
        dropdown above and pick a different input. The interview will not start
        if the AI cannot hear you.
      </p>

      {/* Anti-cheat disclosure - required by NYC LL144 + EU AI Act + general
          informed-consent best practice. The candidate sees this BEFORE
          clicking Join, so monitoring is only ever consensual. */}
      <p style={{
        marginTop: 14, fontSize: 11, color: 'rgba(253, 250, 240, 0.55)',
        maxWidth: 520, textAlign: 'center', lineHeight: 1.5,
      }}>
        This interview is AI-assisted and uses anti-cheat monitoring
        (tab-switching, paste, and DevTools detection). We capture event
        metadata only, never your typed content or clipboard data. By clicking
        Join you consent to this monitoring; you may exit at any time.
      </p>
    </div>
  );
}


// ---- Status screen helper --------------------------------------------------

function FullPageStatus({ title, subtitle, action, isError }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: '#fdfaf0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
    }}>
      <div style={{
        background: isError ? '#fef2f2' : '#fff',
        border: `1px solid ${isError ? '#fecaca' : 'rgba(27, 58, 42, 0.2)'}`,
        borderRadius: 12,
        padding: 24,
        maxWidth: 480,
        textAlign: 'center',
      }}>
        <h2 style={{ margin: '0 0 8px', color: isError ? '#a33' : '#1b3a2a' }}>{title}</h2>
        {subtitle && <p style={{ margin: 0, color: isError ? '#a33' : '#6b6b6b' }}>{subtitle}</p>}
        {action && (
          <button
            onClick={action.onClick}
            style={{
              marginTop: 16,
              padding: '8px 16px',
              background: isError ? '#a33' : '#1b3a2a',
              color: '#fff',
              border: 0,
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
